"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Save, CheckCircle, AlertCircle, X } from "lucide-react";

interface FormActionsProps {
  isCreating: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  hasValidationErrors: boolean;
  submissionError: string | null;
  displayName: string;
  onSubmit: () => void;
  onSaveTemplate?: () => void;
  onCancel?: () => void;
}

/**
 * Component that renders form action buttons:
 * - Create button
 * - Save as Template button (optional)
 * - Cancel button (optional)
 */
export const FormActions: React.FC<FormActionsProps> = ({
  isCreating,
  isLoading,
  isSubmitting,
  hasValidationErrors,
  submissionError,
  displayName,
  onSubmit,
  onSaveTemplate,
  onCancel,
}) => {
  return (
    <div className="pt-2 flex flex-col sm:flex-row gap-3">
      <Button
        type="button"
        onClick={onSubmit}
        disabled={isCreating || isLoading || isSubmitting}
        className="flex-1 sm:flex-none min-w-[140px] text-base sm:text-sm py-3 sm:py-2"
        variant={hasValidationErrors ? "destructive" : "default"}
      >
        {isCreating || isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
            Creating {displayName}...
          </>
        ) : hasValidationErrors ? (
          <>
            <AlertCircle className="h-4 w-4 mr-2" />
            Fix errors to continue
          </>
        ) : submissionError ? (
          <>
            <AlertCircle className="h-4 w-4 mr-2" />
            Retry submission
          </>
        ) : (
          <>
            <CheckCircle className="h-4 w-4 mr-2" />
            Create {displayName}
          </>
        )}
      </Button>

      {onSaveTemplate && (
        <Button
          type="button"
          variant="outline"
          onClick={onSaveTemplate}
          className="flex items-center gap-2 text-base sm:text-sm py-3 sm:py-2"
        >
          <Save className="h-4 w-4" />
          Save as Template
        </Button>
      )}

      {onCancel && (
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex items-center gap-2 text-base sm:text-sm py-3 sm:py-2"
        >
          Cancel
        </Button>
      )}
    </div>
  );
};
